import { QboButton, QboModal, QboSnackbar, QboTypography, TooltipWrapper } from '@ui/Components';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import QboCardSingleList from '@ui/Components/QboCardSingleList';
import useLocalStorage from '@hooks/useLocalStorage';
import { constant } from '@config/BaseSetting';
import { IconButton, Skeleton } from '@mui/material';
import {
  useApproveJobRequestMutation,
  useGetJobRequestDetailsMutation,
  useRejectJobRequestMutation,
  useReviseAndApproveJobRequestMutation,
} from '@services/WebApiService/jobRequestApi';
import useUserClaims from '@hooks/useUserClaims';
import { Close, Edit } from '@mui/icons-material';
import useEligibleServices from '@hooks/useEligibleServices';
import { useDispatch } from 'react-redux';
import { showFixedAlert } from '@features/application/applicationSlice';
import { useTranslation } from 'react-i18next';
import ClientRoutes from '@config/Routes/WebClientRoutes';
import NotFoundSection from '../SystemStatusPage/NotFoundSection';
import RunFullBackupPayloadSection from './RunFullBackupPayloadSection';

const statusDescriptions = {
  1: 'Pending',
  2: 'Approved',
  3: 'Rejected',
};

export default function JobRequestDetailsSection(props) {
  const claims = useUserClaims();
  const { isEligible } = useEligibleServices();
  const { jobId, handleOnClose, serviceTypeNameMapping } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [accessToken] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const [snackbarState, setSnackbarState] = useState({ open: false });
  const [isEditting, setIsEditting] = useState(false);
  const [submitFlag, setSubmitFlag] = useState(false);

  const [getJobRequestDetails, getJobRequestDetailsState] = useGetJobRequestDetailsMutation();
  const [approveJobRequest, approveJobRequestState] = useApproveJobRequestMutation();
  const [reviseAndApproveJobRequest, reviseAndApproveJobRequestState] =
    useReviseAndApproveJobRequestMutation();
  const [rejectJobRequest, rejectJobRequestState] = useRejectJobRequestMutation();

  const isAdmin = useMemo(() => claims.role?.toLowerCase() === 'admin');

  const data = getJobRequestDetailsState.data?.data;

  const handleApprove = () => {
    if (isEditting) setSubmitFlag(true);
    else approveJobRequest({ accessToken, id: jobId });
  };

  const handleReject = () => {
    rejectJobRequest({ accessToken, id: jobId });
  };

  const handlePayloadValidated = (payload) => {
    setIsEditting(false);
    reviseAndApproveJobRequest({ accessToken, id: jobId, payload });
  };

  useEffect(() => {
    if (submitFlag) setSubmitFlag(false);
  }, [submitFlag]);

  const isLoading =
    approveJobRequestState.isLoading ||
    rejectJobRequestState.isLoading ||
    reviseAndApproveJobRequestState.isLoading;

  const loadData = () => {
    if (!jobId) return;
    getJobRequestDetails({
      accessToken,
      id: jobId,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, open: false });
  };

  useEffect(loadData, [jobId]);

  useEffect(() => {
    if (!approveJobRequestState.isSuccess) return;
    const link = ClientRoutes.toolsRequestStatusPath({
      'service-type': data.serviceType,
    });
    dispatch(
      showFixedAlert({
        descriptionHtml: t('job_requests.success_approved', {
          link,
        }),
      })
    );
    approveJobRequestState.reset();
    handleOnClose();
  }, [approveJobRequestState.isSuccess]);

  useEffect(() => {
    if (!reviseAndApproveJobRequestState.isSuccess) return;
    const link = ClientRoutes.toolsRequestStatusPath({
      'service-type': data.serviceType,
    });
    dispatch(
      showFixedAlert({
        descriptionHtml: t('job_requests.success_approved', {
          link,
        }),
      })
    );
    reviseAndApproveJobRequestState.reset();
    handleOnClose();
  }, [reviseAndApproveJobRequestState.isSuccess]);

  useEffect(() => {
    if (!rejectJobRequestState.isSuccess) return;
    rejectJobRequestState.reset();
    handleOnClose();
  }, [rejectJobRequestState.isSuccess]);

  useEffect(() => {
    if (!approveJobRequestState.isError) return;

    const errorMessage =
      approveJobRequestState.error?.data?.message ||
      approveJobRequestState.error?.data?.['status-code'] ||
      approveJobRequestState.error?.status ||
      (approveJobRequestState.error.name === 'FetchError'
        ? 'Network error occurred'
        : 'Unknown error occurred');

    setSnackbarState({
      open: true,
      type: 'error',
      message: errorMessage,
    });

    approveJobRequestState.reset();
  }, [approveJobRequestState.isError]);

  useEffect(() => {
    if (!reviseAndApproveJobRequestState.isError) return;

    const errorMessage =
      reviseAndApproveJobRequestState.error?.data?.message ||
      reviseAndApproveJobRequestState.error?.data?.['status-code'] ||
      reviseAndApproveJobRequestState.error?.status ||
      (reviseAndApproveJobRequestState.error.name === 'FetchError'
        ? 'Network error occurred'
        : 'Unknown error occurred');

    setSnackbarState({
      open: true,
      type: 'error',
      message: errorMessage,
    });

    reviseAndApproveJobRequestState.reset();
  }, [reviseAndApproveJobRequestState.isError]);

  useEffect(() => {
    if (!approveJobRequestState.isError) return;

    const errorMessage =
      rejectJobRequestState.error?.data?.message ||
      `Error ${rejectJobRequestState.error?.data?.['status-code']}` ||
      rejectJobRequestState.error?.status ||
      (rejectJobRequestState.error.name === 'FetchError'
        ? 'Network error occurred'
        : 'Unknown error occurred');

    setSnackbarState({
      open: true,
      type: 'error',
      message: errorMessage,
    });

    rejectJobRequestState.reset();
  }, [rejectJobRequestState.isError]);

  const canEdit = data?.status === 1 && (isAdmin || isEligible(data.serviceType));

  const renderLoading = () => {
    return (
      <div style={{ display: 'contents' }}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="70%" />
      </div>
    );
  };

  const renderPayload = () => {
    if (data.serviceType === constant.SERVICE_IDS.RUN_FULL_BACKUP) {
      return (
        <RunFullBackupPayloadSection
          submitFlag={submitFlag}
          isEditting={isEditting}
          original={data.payload}
          onPayloadValidated={handlePayloadValidated}
        />
      );
    }
    return (
      <pre
        style={{
          textWrap: 'wrap',
          wordWrap: 'break-word',
          wordBreak: 'break-all',
          padding: 20,
          backgroundColor: '#f2f0f0',
          fontSize: '1.5rem',
          lineHeight: '1.6',
        }}>
        {JSON.stringify(data.payload, null, 2)}
      </pre>
    );
  };

  return (
    <QboModal
      onClose={handleOnClose}
      open={!!jobId}
      title="Job Request Details"
      showCloseIcon
      preventOutsideClick>
      {getJobRequestDetailsState.isLoading && renderLoading()}
      {!getJobRequestDetailsState.isLoading && data && (
        <>
          <QboSnackbar onClose={handleSnackbarClose} {...snackbarState} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <QboButton
              style={{ marginBottom: '1rem', marginRight: '1rem' }}
              variant="contained"
              onClick={loadData}>
              Refresh
            </QboButton>

            {canEdit && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <QboButton
                  disabled={isLoading}
                  style={{
                    marginBottom: '1rem',
                    marginRight: '1rem',
                  }}
                  color="error"
                  variant="contained"
                  onClick={handleReject}>
                  Reject
                </QboButton>
                <QboButton
                  disabled={isLoading}
                  style={{ marginBottom: '1rem', marginRight: '1rem' }}
                  variant="contained"
                  onClick={handleApprove}>
                  {isEditting ? 'Apply Revisions and Approve' : 'Approve'}
                </QboButton>
              </div>
            )}
          </div>
          <QboCardSingleList leftContent="ID" rightContent={data.id} />
          <QboCardSingleList leftContent="Service Type ID" rightContent={data.serviceType} />
          <QboCardSingleList
            leftContent="Service Type"
            rightContent={serviceTypeNameMapping[data.serviceType] || '-'}
          />
          <QboCardSingleList
            leftContent="Status"
            rightContent={statusDescriptions[data.status] || data.status}
          />
          <QboCardSingleList leftContent="Requested By" rightContent={data.requestedBy || '-'} />
          <QboCardSingleList leftContent="Requested At" rightContent={data.requestedAt || '-'} />
          <QboCardSingleList
            leftContent="Approved / Rejected By"
            rightContent={data.approvedOrRejectedBy}
          />
          <QboCardSingleList
            leftContent="Approved / Rejected At"
            rightContent={data.approvedOrRejectedAt}
          />
          <>
            <div
              style={{
                margin: '2rem 0 1rem 0',
                display: 'flex',
                alignItems: 'center',
                gap: '1.2rem',
              }}>
              <QboTypography style={{ fontSize: '2rem' }}>Payload</QboTypography>
              {canEdit && (
                <TooltipWrapper condition title={isEditting ? 'Cancel' : 'Make Revisions'}>
                  <IconButton
                    style={{ width: 'max-content' }}
                    sx={{
                      position: 'relative',
                      right: '0.7rem',
                    }}
                    onClick={() => setIsEditting(!isEditting)}>
                    {isEditting ? (
                      <Close sx={{ width: '2rem', height: '2rem' }} />
                    ) : (
                      <Edit sx={{ width: '2rem', height: '2rem' }} />
                    )}
                  </IconButton>
                </TooltipWrapper>
              )}
            </div>
            {renderPayload()}
          </>
          {canEdit && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <QboButton
                disabled={isLoading}
                style={{
                  marginBottom: '1rem',
                  marginRight: '1rem',
                }}
                color="error"
                variant="contained"
                onClick={handleReject}>
                Reject
              </QboButton>
              <QboButton
                disabled={isLoading}
                style={{ marginBottom: '1rem', marginRight: '1rem' }}
                variant="contained"
                onClick={handleApprove}>
                {isEditting ? 'Apply Revisions and Approve' : 'Approve'}
              </QboButton>
            </div>
          )}
        </>
      )}
      {!getJobRequestDetailsState.isLoading && !data && <NotFoundSection />}
    </QboModal>
  );
}

JobRequestDetailsSection.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  handleOnClose: PropTypes.func,
  serviceTypeNameMapping: PropTypes.object,
};

JobRequestDetailsSection.defaultProps = {
  handleOnClose: () => {},
  serviceTypeNameMapping: {},
};
